import Header from "@/components/Header";
import { useContent } from "@/hooks/useContent";

const DEFAULT_TERMS = `<p>Welcome to our booking platform! Please read these terms and conditions carefully before using our service.</p>

<h2>Definitions</h2>
<ul>
<li><strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for the election of directors or other managing authority.</li>
<li><strong>The Company</strong> is referred to as either "the Company", "We", "Us" or "Our" in this Agreement.</li>
<li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
<li><strong>Service</strong> refers to the Website.</li>
<li><strong>Terms and Conditions</strong> (also referred as "Terms") mean these Terms and Conditions that form the entire agreement between the parties regarding the use of the Service.</li>
<li><strong>Third-party Social Media Service</strong> means any services or content (including data, information, products or services) provided by a third-party that may be displayed, included or made available by the Service.</li>
<li><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
</ul>

<h2>Acknowledgment</h2>
<p>These are the Terms and Conditions governing the use of this Service and the agreement that operates between the parties. These Terms and Conditions set out the rights and obligations of all users regarding the use of the Service.</p>
<p>Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms and Conditions. These Terms and Conditions apply to all visitors, users and others who access or use the Service.</p>
<p>By accessing or using the Service you agree to be bound by these Terms and Conditions. If You disagree with any part of these Terms and Conditions then You may not access the Service.</p>
<p>You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service.</p>
<p>Your access to and use of the Service is also conditioned on your acceptance of and compliance with the Privacy Policy of the company. Our Privacy Policy describes our policies and procedures on the collection, use and disclosure of Your personal information when You use the Application or the Website and tells You about Your privacy rights and how the law protects You. Please read Our Privacy Policy carefully before using Our Service.</p>

<h2>Links to Other Websites</h2>
<p>Our Service may contain links to third-party web sites or services that are not owned or controlled by the Company.</p>
<p>The Company has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services. You further acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods or services available on or through any such web sites or services.</p>
<p>We strongly advise You to read the terms and conditions and privacy policies of any third-party web sites or services that You visit.</p>

<h2>Termination</h2>
<p>We may terminate or suspend Your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions.</p>
<p>Upon termination, Your right to use the Service will cease immediately.</p>

<h2>Limitation of Liability</h2>
<p>Notwithstanding any damages that You might incur, the entire liability of the Company and any of its suppliers under any provision of this Terms and Your exclusive remedy for all of the foregoing shall be limited to the amount actually paid by You through the Service or 100 USD if You haven't purchased anything through the Service.</p>
<p>To the maximum extent permitted by applicable law, in no event shall the Company or its suppliers be liable for any special, incidental, indirect, or consequential damages whatsoever.</p>

<h2>"AS IS" and "AS AVAILABLE" Disclaimer</h2>
<p>The Service is provided to You "AS IS" and "AS AVAILABLE" and with all faults and defects without warranty of any kind.</p>

<h2>Governing Law</h2>
<p>The laws of the Company's incorporation place, excluding its conflicts of law rules, shall govern these Terms and Your use of the Service.</p>

<h2>Disputes Resolution</h2>
<p>If You have any concern or dispute about the Service, You agree to first try to resolve the dispute informally by contacting the Company.</p>

<h2>Severability and Waiver</h2>
<p>If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.</p>

<h2>Changes to These Terms and Conditions</h2>
<p>We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. If a revision is material We will make reasonable efforts to provide at least 30 days' notice prior to any new terms taking effect.</p>
<p>By continuing to access or use Our Service after those revisions become effective, You agree to be bound by the revised terms.</p>

<h2>Contact Us</h2>
<p>If you have any questions about these Terms and Conditions, You can contact us:</p>
<p>By email: <a href="mailto:info@kspaceproperties.com">info@kspaceproperties.com</a></p>`;

const Terms = () => {
  const { content: c } = useContent();
  const termsHtml = c.terms_content || DEFAULT_TERMS;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-heading font-bold text-primary mb-8" data-testid="terms-title">
            Terms and Conditions
          </h1>
          
          <div 
            className="prose max-w-none text-slate-700 space-y-4 [&_h2]:text-2xl [&_h2]:font-heading [&_h2]:font-semibold [&_h2]:text-primary [&_h2]:mt-8 [&_h2]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:text-accent [&_a]:hover:underline"
            dangerouslySetInnerHTML={{ __html: termsHtml }}
            data-testid="terms-content"
          />
        </div>
      </div>

      <footer className="bg-secondary py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-heading font-semibold text-primary mb-4">{c.footer_company || "KSpace Properties"}</h3>
          <p className="text-slate-600 mb-4">{c.footer_email || "info@kspaceproperties.com"}</p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
